import { useCallback, useEffect, useRef, useState } from 'react';
import { SheetCatalog } from '../types/sheets';
import { fetchSheetCatalog } from '../utils/sheetsCatalog';

export type SheetStatus = 'loading' | 'ready' | 'error';

export interface SheetCatalogState {
  status: SheetStatus;
  data: SheetCatalog | null;
  lastSync: string | null;
  error: string | null;
  refresh: () => void;
}

const CACHE_KEY = 'vcars-sheet-catalog-v1';
const REFRESH_MS = 5 * 60 * 1000;
const MIN_GAP_MS = 60 * 1000;

interface Cached {
  data: SheetCatalog;
  at: string;
}

function readCache(): Cached | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    return raw ? (JSON.parse(raw) as Cached) : null;
  } catch {
    return null;
  }
}

function writeCache(c: Cached) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(c));
  } catch {
    // almacenamiento no disponible
  }
}

/** Catálogo de la planilla: muestra la última copia guardada al instante y se actualiza en segundo plano. */
export function useSheetCatalog(): Omit<SheetCatalogState, 'refresh'> & { refresh: () => void } {
  const [state, setState] = useState<Omit<SheetCatalogState, 'refresh'>>(() => {
    const cached = readCache();
    return { status: cached ? 'ready' : 'loading', data: cached?.data ?? null, lastSync: cached?.at ?? null, error: null };
  });
  const ctrlRef = useRef<AbortController | null>(null);
  const lastAttempt = useRef(0);

  const load = useCallback(async () => {
    lastAttempt.current = Date.now();
    ctrlRef.current?.abort();
    const ctrl = new AbortController();
    ctrlRef.current = ctrl;
    try {
      const data = await fetchSheetCatalog(ctrl.signal);
      const at = new Date().toISOString();
      writeCache({ data, at });
      setState({ status: 'ready', data, lastSync: at, error: null });
    } catch (e) {
      if (ctrl.signal.aborted) return;
      const message = e instanceof Error ? e.message : 'No se pudo conectar con la planilla';
      setState((s) => ({ ...s, status: s.data ? 'ready' : 'error', error: message }));
    }
  }, []);

  useEffect(() => {
    load();
    const timer = window.setInterval(load, REFRESH_MS);
    const onVisible = () => {
      if (document.visibilityState === 'visible' && Date.now() - lastAttempt.current > MIN_GAP_MS) load();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
      ctrlRef.current?.abort();
    };
  }, [load]);

  return { ...state, refresh: load };
}
